import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import RedoIcon from '@mui/icons-material/Redo';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { Avatar, Menu, MenuItem } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import ColorConstants from '../defaults/ColorConstants';
import { doesFilterContainFundingOrgs } from './filter/customFilters/FilterHelper';
import { displayBulkApprovalFilterWarning } from '../utils/BulkApprovalHelper';
import { ModeEditOutlineTwoTone } from '@mui/icons-material';
import FieldNames from '@/defaults/FieldNames.json';

export default function WorkflowMenu({ anchorEl, open, handleClose, userRoleInfo }) {
    const [manageBudgetHover, setManageBudgetHover] = useState(false);
    const [manageCarryoverHover, setManageCarryoverHover] = useState(false);
    const [manageDownloadHover, setManageDownloadHover] = useState(false);
    const [targetAccomplishmentHover, setTargetAccomplishmentHover] = useState(false);
    const [summaryHover, setSummaryHover] = useState(false);
    const [bulkApprovalHover, setBulkApprovalHover] = useState(false);
    const [bulkEditHover, setBulkEditHover] = useState(false);
    const navigate = useNavigate();
    const definitionExpression = useSelector((state) => state.filter.definitionExpression);

    // TODO: Performance concern around how many re-renders will be caused by this global context pull
    //        until we can split apart the global context in FUELS-1331

    const handleNavigateBudgetAction = () => {
        handleClose();
        setManageBudgetHover(false);
        navigate('/workflow/budget');
    };

    const handleNavigateCarryoverAction = () => {
        handleClose();
        setManageCarryoverHover(false);
        navigate('/workflow/carryover');
    };

    const handleNavigateDownloadAction = () => {
        handleClose();
        setManageDownloadHover(false);
        navigate('/workflow/downloads');
    };

    const handleNavigateTargetAccomplishmentAction = () => {
        handleClose();
        setTargetAccomplishmentHover(false);
        navigate('/workflow/targets');
    };

    const handleNavigateSummaryAction = () => {
        handleClose();
        setSummaryHover(false);
        navigate('/workflow/reporting');
    };

    const handleNavigateBulkApprovalAction = () => {
        setBulkApprovalHover(false);
        if (doesFilterContainFundingOrgs(definitionExpression)) {
            navigate('/workflow/bulkapproval');
        } else {
            displayBulkApprovalFilterWarning(handleClose);
        }
    };

    const handleNavigateBulkEditAction = () => {
        handleClose();
        setBulkEditHover(false);
        navigate('/workflow/bulkedit');
    };

    return (
        <Menu
            id='basic-menu'
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
                'aria-labelledby': 'basic-button',
            }}
        >
            <MenuItem
                onClick={handleNavigateTargetAccomplishmentAction}
                onMouseEnter={() => {
                    setTargetAccomplishmentHover(true);
                }}
                onMouseLeave={() => {
                    setTargetAccomplishmentHover(false);
                }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        margin: '0 10px 0 0',
                        padding: 0,
                        backgroundColor: targetAccomplishmentHover
                            ? ColorConstants.BlueAlien
                            : ColorConstants.AvatarGray,
                    }}
                >
                    <LeaderboardIcon></LeaderboardIcon>
                </Avatar>{' '}
                Targets & Accomplishments
            </MenuItem>
            <MenuItem
                onClick={handleNavigateBudgetAction}
                onMouseEnter={() => {
                    setManageBudgetHover(true);
                }}
                onMouseLeave={() => {
                    setManageBudgetHover(false);
                }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        margin: '0 10px 0 0',
                        padding: 0,
                        backgroundColor: manageBudgetHover
                            ? ColorConstants.BlueAlien
                            : ColorConstants.AvatarGray,
                    }}
                >
                    <PriceCheckIcon></PriceCheckIcon>
                </Avatar>{' '}
                Manage Budget Levels
            </MenuItem>
            <MenuItem
                onClick={handleNavigateCarryoverAction}
                onMouseEnter={() => {
                    setManageCarryoverHover(true);
                }}
                onMouseLeave={() => {
                    setManageCarryoverHover(false);
                }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        margin: '0 10px 0 0',
                        padding: 0,
                        backgroundColor: manageCarryoverHover
                            ? ColorConstants.BlueAlien
                            : ColorConstants.AvatarGray,
                    }}
                >
                    <RedoIcon></RedoIcon>
                </Avatar>{' '}
                Carryover
            </MenuItem>
            {
                <MenuItem
                    onClick={handleNavigateDownloadAction}
                    onMouseEnter={() => {
                        setManageDownloadHover(true);
                    }}
                    onMouseLeave={() => {
                        setManageDownloadHover(false);
                    }}
                >
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            margin: '0 10px 0 0',
                            padding: 0,
                            backgroundColor: manageDownloadHover
                                ? ColorConstants.BlueAlien
                                : ColorConstants.AvatarGray,
                        }}
                    >
                        <SimCardDownloadIcon></SimCardDownloadIcon>
                    </Avatar>{' '}
                    Downloads
                </MenuItem>
            }
            <MenuItem
                onClick={handleNavigateSummaryAction}
                onMouseEnter={() => {
                    setSummaryHover(true);
                }}
                onMouseLeave={() => {
                    setSummaryHover(false);
                }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        margin: '0 10px 0 0',
                        padding: 0,
                        backgroundColor: summaryHover
                            ? ColorConstants.BlueAlien
                            : ColorConstants.AvatarGray,
                    }}
                >
                    <DonutSmallIcon></DonutSmallIcon>
                </Avatar>{' '}
                Summaries
            </MenuItem>
            <MenuItem
                onClick={handleNavigateBulkApprovalAction}
                onMouseEnter={() => {
                    setBulkApprovalHover(true);
                }}
                onMouseLeave={() => {
                    setBulkApprovalHover(false);
                }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        margin: '0 10px 0 0',
                        padding: 0,
                        backgroundColor: bulkApprovalHover
                            ? ColorConstants.BlueAlien
                            : ColorConstants.AvatarGray,
                    }}
                >
                    <ChecklistRtlIcon />
                </Avatar>{' '}
                Bulk Approval
            </MenuItem>
            { userRoleInfo?.length > 0 && (userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.department || userRoleInfo[0][FieldNames.userRoles.appRole] === FieldNames.roles.agency) ?
            (<MenuItem
                onClick={handleNavigateBulkEditAction}
                onMouseEnter={() => {
                    setBulkEditHover(true);
                }}
                onMouseLeave={() => {
                    setBulkEditHover(false);
                }}
            >
                <Avatar
                    sx={{
                        width: 32,
                        height: 32,
                        margin: '0 10px 0 0',
                        padding: 0,
                        backgroundColor: bulkEditHover
                            ? ColorConstants.BlueAlien
                            : ColorConstants.AvatarGray,
                    }}
                >
                    <ModeEditOutlineTwoTone />
                </Avatar>{' '}
                Bulk Edit
            </MenuItem>) : null }
        </Menu>
    );
}

WorkflowMenu.propTypes = {
    anchorEl: PropTypes.any,
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
};
